import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { Stack, Chip } from "@mui/material";
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  }
}));

const FlowBuilderAddTagModal = ({
  open,
  onSave,
  data,
  onUpdate,
  close
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const [activeModal, setActiveModal] = useState(false);
  const [tags, setTags] = useState([]);
  const [novaTag, setNovaTag] = useState("");
  const [novaCor, setNovaCor] = useState("#10b981");
  const [criandoTag, setCriandoTag] = useState(false);

  // Cria a tag sem sair do fluxo: evita ter que abrir a tela de tags so para isso.
  const criarTag = async () => {
    const nome = String(novaTag || "").trim();
    if (!nome) {
      toast.error("Informe o nome da tag.");
      return;
    }
    if (tags.some((t) => String(t.name).toLowerCase() === nome.toLowerCase())) {
      toast.error("Já existe uma tag com esse nome.");
      return;
    }
    setCriandoTag(true);
    try {
      const { data: criada } = await api.post("/tags", {
        name: nome,
        color: novaCor,
        kanban: 0,
      });
      const { data: lista } = await api.get("/tags");
      setTags(lista.tags || lista || []);
      if (criada?.id) setSelectedTag(criada.id);
      setNovaTag("");
      toast.success("Tag criada.");
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "Não foi possível criar a tag."
      );
    }
    setCriandoTag(false);
  };

  const [selectedTag, setSelectedTag] = useState("");

  useEffect(() => {
    if (open === 'edit') {
      (async () => {
        try {
          const { data: tagsData } = await api.get("/tags");
          setTags(tagsData.tags || tagsData || []);
          if (data?.data?.id) {
            setSelectedTag(data.data.id);
          }
          setActiveModal(true);
        } catch (error) {
          console.log(error);
        }
      })();
    } else if (open === 'create') {
      (async () => {
        try {
          const { data: tagsData } = await api.get("/tags");
          setTags(tagsData.tags || tagsData || []);
          setSelectedTag("");
          setActiveModal(true);
        } catch (error) {
          console.log(error);
        }
      })();
    }
    return () => {
      isMounted.current = false;
    };
  }, [open]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSave = () => {
    if (!selectedTag) {
      return toast.error('Selecione uma tag');
    }
    const tag = tags.find(item => item.id === selectedTag);
    if (open === 'edit') {
      onUpdate({
        ...data,
        data: { id: tag.id, name: tag.name, color: tag.color }
      });
    } else if (open === 'create') {
      onSave({
        data: { id: tag.id, name: tag.name, color: tag.color }
      });
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog open={activeModal} onClose={handleClose} fullWidth maxWidth="sm" scroll="paper">
        <DialogTitle id="form-dialog-title">
          {open === 'create' ? `Adicionar tag ao contato` : `Editar tag`}
        </DialogTitle>
        <Stack>
          <DialogContent dividers>
            <Select
              labelId="tag-select-label"
              id="tag-select"
              value={selectedTag}
              style={{ width: "100%" }}
              onChange={(e) => setSelectedTag(e.target.value)}
              displayEmpty
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
              }}
              renderValue={(selected) => {
                if (!selected) {
                  return <span style={{ color: "#999" }}>Selecione uma tag</span>;
                }
                const tag = tags.find(t => t.id === selected);
                return tag ? (
                  <Chip
                    label={tag.name}
                    size="small"
                    style={{ backgroundColor: tag.color, color: "#fff" }}
                  />
                ) : "";
              }}
            >
              <MenuItem value="" disabled>
                Selecione uma tag
              </MenuItem>
              {tags.length > 0 && (
                tags.map((tag, index) => (
                  <MenuItem dense key={index} value={tag.id}>
                    <Chip
                      label={tag.name}
                      size="small"
                      style={{ backgroundColor: tag.color, color: "#fff", marginRight: 8 }}
                    />
                    {tag.name}
                  </MenuItem>
                ))
              )}
            </Select>

            <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                Criar uma tag nova
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  value={novaTag}
                  onChange={(e) => setNovaTag(e.target.value)}
                  placeholder="Nome da tag"
                  maxLength={40}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      criarTag();
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: "8px 10px",
                    borderRadius: 6,
                    border: "1px solid #cbd5e1",
                    fontSize: 13,
                    outline: "none",
                  }}
                />
                <input
                  type="color"
                  value={novaCor}
                  onChange={(e) => setNovaCor(e.target.value)}
                  title="Cor da tag"
                  style={{
                    width: 40,
                    height: 34,
                    border: "1px solid #cbd5e1",
                    borderRadius: 6,
                    padding: 2,
                    cursor: "pointer",
                    background: "transparent",
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={criarTag}
                  disabled={criandoTag}
                  style={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  {criandoTag ? "Criando..." : "Criar"}
                </Button>
              </div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}>
                A tag é criada na conta e já fica selecionada aqui.
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleClose}
              startIcon={<CancelIcon />}
              style={{
                color: "white",
                backgroundColor: "#db6565",
                boxShadow: "none",
                borderRadius: 0,
                fontSize: "12px",
              }}
              variant="outlined"
            >
              {i18n.t("contactModal.buttons.cancel")}
            </Button>
            <Button
              startIcon={<SaveIcon />}
              type="submit"
              style={{
                color: "white",
                backgroundColor: "#437db5",
                boxShadow: "none",
                borderRadius: 0,
                fontSize: "12px",
              }}
              variant="contained"
              className={classes.btnWrapper}
              onClick={handleSave}
            >
              {open === 'create' ? `Adicionar` : 'Salvar'}
            </Button>
          </DialogActions>
        </Stack>
      </Dialog>
    </div>
  );
};

export default FlowBuilderAddTagModal;
